import classNames from 'classnames';
import dayjs from 'dayjs';
import { type AwaitingSignatureDto } from 'xosign-types';
import Button from '@/components/Button';
import getInitials from '@/utils/getInitials';
import IcTimer from '@/assets/icons/Timer.svg?react';
import buildSignUrl from './buildSignUrl.util';
import { isDueToday, isOverdue } from './deadline.util';
import styles from './AwaitingSignatures.module.scss';

interface RowProps {
  item: AwaitingSignatureDto;
  dateFormatter: Intl.DateTimeFormat;
  dayjsLocale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const Row = ({ item, dateFormatter, dayjsLocale, t }: RowProps) => {
  const dueToday = isDueToday(item.expirationDate);
  const overdue = isOverdue(item.expirationDate);
  const senderName = item.createdBy.name || '';

  // .locale(...) per-instance so the rendered string depends on the prop —
  // otherwise the React Compiler memoizes the result of dayjs().fromNow() by
  // (sentAt) alone and never updates when the user switches language.
  const timeAgo = dayjs(item.sentAt).locale(dayjsLocale).fromNow();

  return (
    <li
      className={classNames(styles.row, {
        [styles.rowUrgent]: dueToday || overdue,
      })}
    >
      <span className={styles.avatar} aria-hidden="true">
        {getInitials(senderName)}
      </span>
      <div className={styles.meta}>
        <div className={styles.title}>{item.name}</div>
        <div className={styles.sender}>
          {t('common:dashboard.awaiting.from', { name: senderName })}
        </div>
      </div>
      <div className={styles.time}>
        <IcTimer className={styles.timeIcon} aria-hidden="true" />
        <span>{timeAgo}</span>
      </div>
      {item.expirationDate && (
        <span
          className={classNames(styles.deadline, {
            [styles.deadlineUrgent]: dueToday || overdue,
          })}
        >
          {overdue
            ? t('common:dashboard.awaiting.overdue')
            : dueToday
              ? t('common:dashboard.awaiting.dueToday')
              : t('common:dashboard.awaiting.dueBy', {
                  date: dateFormatter.format(new Date(item.expirationDate)),
                })}
        </span>
      )}
      <Button
        as="link"
        to={buildSignUrl(item.id, item.signerId)}
        target="_blank"
        className={styles.cta}
      >
        {t('common:dashboard.awaiting.cta')}
      </Button>
    </li>
  );
};

export default Row;
